// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Emergency Fuel System
 * Provides safety nets for players who run out of fuel
 */

export class EmergencyFuelSystem {
  /**
   * Call distress beacon - tow to nearest station
   */
  static callDistressBeacon(currentLocation, allSystems, credits) {
    const COST = 5000;
    
    if (credits < COST) {
      return { 
        success: false, 
        reason: 'Insufficient credits',
        required: COST,
        current: credits
      };
    }
    
    // Find nearest station with refuel
    const nearestStation = this.findNearestRefuelLocation(currentLocation, allSystems);
    
    if (!nearestStation) {
      return {
        success: false,
        reason: 'No stations in range'
      };
    }
    
    return {
      success: true,
      destination: nearestStation,
      cost: COST,
      time: 60 // seconds
    };
  }
  
  /**
   * Synthesize fuel from power (emergency only)
   */
  static synthesizeFuel(currentPower) {
    const MIN_POWER = 50;
    const CONVERSION_RATE = 0.2; // 50% power → 10% fuel
    
    if (currentPower < MIN_POWER) {
      return {
        success: false,
        reason: 'Insufficient power',
        required: MIN_POWER,
        current: currentPower
      };
    }
    
    const powerCost = 50;
    const fuelGained = powerCost * CONVERSION_RATE;
    
    return {
      success: true,
      fuelGained,
      powerCost,
      cooldown: 300 // 5 minutes
    };
  }
  
  /**
   * Solar charging near stars
   */
  static checkSolarCharging(currentLocation, nearestStar) {
    const MAX_DISTANCE = 5; // LY
    
    if (!nearestStar || nearestStar.distance > MAX_DISTANCE) {
      return {
        available: false,
        reason: 'Too far from star'
      };
    }
    
    const rate = 0.1; // 0.1% per minute
    const efficiency = Math.max(0.1, 1 - (nearestStar.distance / MAX_DISTANCE));
    
    return {
      available: true,
      rate: rate * efficiency,
      distance: nearestStar.distance,
      starName: nearestStar.name
    };
  }
  
  /**
   * Find nearest star system for solar charging
   */
  static findNearestStar(currentLocation, allSystems) {
    if (!currentLocation || !allSystems?.length) return null;
    let nearest = null;
    let minDist = Infinity;
    allSystems.forEach(sys => {
      const coords = sys.coordinates;
      if (!coords) return;
      const dist = this.calculateDistance(currentLocation, coords);
      if (dist < minDist) {
        minDist = dist;
        nearest = { distance: dist, name: sys.name };
      }
    });
    return nearest;
  }

  /**
   * Find nearest location with refuel service
   */
  static findNearestRefuelLocation(currentLocation, allSystems) {
    const refuelLocations = allSystems.filter(sys => 
      sys.services && sys.services.includes('refuel')
    );
    
    if (refuelLocations.length === 0) return null;
    
    // Sort by distance
    const sorted = refuelLocations
      .map(sys => ({
        ...sys,
        distanceFrom: this.calculateDistance(currentLocation, sys.coordinates)
      }))
      .sort((a, b) => a.distanceFrom - b.distanceFrom);
    
    return sorted[0];
  }
  
  /**
   * Calculate distance between two points
   */
  static calculateDistance(pos1, pos2) {
    const dx = pos2.x - pos1.x;
    const dy = pos2.y - pos1.y;
    const dz = (pos2.z || 0) - (pos1.z || 0);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
}

export default EmergencyFuelSystem;
